import { Node, mergeAttributes } from '@tiptap/core'
import { Plugin, PluginKey } from 'prosemirror-state'
import { buildResourceTableInsertContent } from '../lib/post_block_indent.js'
import { syncResourceRowParagraphsForResource } from '../lib/sync_tiptap_resource_row_titles.js'
import {
  afterResourceRowInsertFocus,
  paragraphRowRole,
  resolveResourceTableRowInsert,
  resourceRowTitleText,
  RESOURCE_ROW_TITLE_PARAGRAPH_ATTRS,
} from '../lib/resource_row_paragraph.js'

// Debounce utility for resource title updates (same behaviour as download row)
const createDebouncedResourceUpdate = () => {
  const timeouts = new Map()

  return (resourceId, newTitle) => {
    if (!resourceId) return
    if (!newTitle || typeof newTitle !== 'string' || newTitle.trim().length === 0) {
      if (timeouts.has(resourceId)) {
        clearTimeout(timeouts.get(resourceId))
        timeouts.delete(resourceId)
      }
      return
    }
    if (timeouts.has(resourceId)) {
      clearTimeout(timeouts.get(resourceId))
    }
    const timeout = setTimeout(async () => {
      timeouts.delete(resourceId)
      const csrfToken = document.querySelector('meta[name=csrf-token]')?.getAttribute('content') || ''
      try {
        const trimmedTitle = newTitle.trim()
        if (trimmedTitle.length === 0) return
        const response = await fetch(`/admin/resources/${resourceId}.json`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({ resource: { title: trimmedTitle } })
        })
        if (!response.ok) {
          console.warn('Failed to update resource title:', await response.text())
          return
        }
        syncResourceRowParagraphsForResource('audioRow', resourceId, trimmedTitle)
      } catch (error) {
        console.error('Error updating resource title:', error)
      }
    }, 1500)
    timeouts.set(resourceId, timeout)
  }
}

const debouncedResourceUpdate = createDebouncedResourceUpdate()

export const AudioExtension = Node.create({
  name: 'audio',

  group: 'block',

  content: 'audioRow+',

  defining: true,

  addAttributes() {
    return {
      href: { default: null },
      src: { default: null },
      filename: { default: null },
      contentType: { default: null },
      signedId: { default: null },
      rowId: { default: null },
    }
  },

  parseHTML() {
    return []
  },

  renderHTML() {
    // no outer container rendering
    return ['div']
  },

  addCommands() {
    return {
      setAudio:
        (options) =>
        ({ commands, editor, state }) => {
          const title = options && (options.title || options.filename) ? (options.title || options.filename) : 'Click to edit title';
          const ok = commands.insertContent(
            buildResourceTableInsertContent(state, [
              {
                type: 'audioRow',
                attrs: options,
                content: [
                  {
                    type: 'paragraph',
                    attrs: RESOURCE_ROW_TITLE_PARAGRAPH_ATTRS,
                    content: [{ type: 'text', text: title }],
                  },
                ],
              },
            ])
          );
          if (ok && editor) {
            afterResourceRowInsertFocus(editor, state, {
              resourceId: options?.resourceId,
              rowType: 'audioRow',
            });
          }
          return ok;
        },

      addAudioRow:
        (options) =>
        ({ commands, editor, state }) => {
          const insertCtx = resolveResourceTableRowInsert(state);
          const title = options && (options.title || options.filename) ? (options.title || options.filename) : 'Click to edit title';
          const ok = commands.insertContent({
            type: 'audioRow',
            attrs: options,
            content: [
              {
                type: 'paragraph',
                attrs: RESOURCE_ROW_TITLE_PARAGRAPH_ATTRS,
                content: [{ type: 'text', text: title }],
              },
            ],
          });
          if (ok && editor) {
            afterResourceRowInsertFocus(editor, state, {
              resourceId: options?.resourceId,
              rowType: 'audioRow',
              insertCtx,
            });
          }
          return ok;
        },
    }
  },
})

export const AudioRowExtension = Node.create({
  name: 'audioRow',

  group: 'block',

  content: 'paragraph+',

  defining: true,

  addAttributes() {
    return {
      href: { default: null },
      src: { default: null },
      filename: { default: null },
      contentType: { default: null },
      signedId: { default: null },
      rowId: { default: null },
      resourceId: { default: null },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'tr[data-resource-row]',
        priority: 100,
        getAttrs: element => {
          const href = element.getAttribute('data-href') || element.getAttribute('href') || null
          const resourceId = element.getAttribute('data-resource-id') || null
          const filename = element.getAttribute('data-file-name') || null
          const contentType = element.getAttribute('data-content-type') || null
          const signedId = element.getAttribute('data-signed-id') || null
          const isAudio =
            element.querySelector('img[src*="audio-icon.svg"]') ||
            element.querySelector('.icon-ps-audio') ||
            element.querySelector('audio') ||
            element.getAttribute('data-row-type') === 'audio'
          return isAudio ? { href, resourceId, filename, contentType, signedId, rowType: 'audio' } : false
        },
        contentElement: element => element.querySelector('td:last-child'),
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    const { href, filename, contentType, signedId, rowId, resourceId } = HTMLAttributes

    const elementAttrs = mergeAttributes(HTMLAttributes, {
      'data-resource-row': '',
      'data-row-type': 'audio',
      'data-disposition': 'inline',
    })

    if (signedId) {
      elementAttrs['data-blob-signed-id'] = signedId
      elementAttrs['data-blob-attrs'] = 'href,data-href'
    }

    // Attach metadata as data-attributes so frontend upload handler can read them (same as download/image/video)
    if (filename) elementAttrs['data-file-name'] = filename
    if (contentType) elementAttrs['data-content-type'] = contentType
    if (signedId) elementAttrs['data-signed-id'] = signedId
    if (rowId) elementAttrs['data-row-id'] = rowId
    if (resourceId) elementAttrs['data-resource-id'] = resourceId.toString()

    const formattedUrl = (href && href !== 'null')
      ? href
      : (signedId && filename)
        ? `/rails/active_storage/blobs/${signedId}/${encodeURIComponent(filename)}`
        : (filename || null)

    if (formattedUrl) {
      elementAttrs['data-href'] = formattedUrl
      elementAttrs['href'] = formattedUrl
    }

    // Build an audio player element and make the last cell editable (0)
    return [
      'tr',
      elementAttrs,
      [
        'td',
        {
          colspan: '1',
          rowspan: '1',
        },
        [
          'span',
          {
            class: 'ps-resource-row__icon',
            draggable: 'false',
            contenteditable: 'false',
            'data-resource-row-icon': 'audio',
          },
          ['span', { class: 'icon-ps-audio', 'aria-hidden': 'true' }],
        ],
      ],
      [
        'td',
        {
          colspan: '1',
          rowspan: '1',
          class: 'audio-title',
        },
        [
          'div',
          { class: 'audio-player-wrap font-primary-bold' },
          [
            'audio',
            formattedUrl ? { controls: 'controls', src: formattedUrl } : { controls: 'controls' }
          ],
          // The editable content (paragraph) must be rendered as the sole child of its container
          // to satisfy ProseMirror's "content hole must be the only child" requirement.
          ['div', { class: 'audio-editable-wrap' }, 0],
        ]
      ],
    ]
  },

  addCommands() {
    return {}
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('audioRowKeyupHandler'),
        props: {
          handleDOMEvents: {
            keyup: (view, event) => {
              const { state } = view
              const { selection } = state
              const { $from } = selection

              for (let depth = $from.depth; depth >= 0; depth--) {
                const node = $from.node(depth)
                if (node.type.name === 'audioRow') {
                  const resourceId = node.attrs.resourceId
                  if (resourceId && paragraphRowRole($from.parent) !== 'subtitle') {
                    const newTitle = resourceRowTitleText(node)
                    if (newTitle.length > 0) {
                      debouncedResourceUpdate(resourceId, newTitle)
                    } else {
                      debouncedResourceUpdate(resourceId, '')
                    }
                  }
                  break
                }
              }
              return false
            }
          }
        }
      })
    ]
  },

})

export default AudioExtension
